import React, { FC, useRef, useMemo, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { FIELD_SEARCH } from '../../constants/form'
import { ROUTES } from '../../enums/routes'
import { iconList } from '../../assets/images/images'
import DynamicFormattedMessage from '../common/ui/DynamicFormattedMessage'
import { ISearchSection } from '../../interfaces/IMap'
import { Tooltip } from '../common/Tooltip/Tooltip'
import { TooltipContent } from '../common/Tooltip/TooltipContent'
import { MAP } from '../../constants/routes'
import { INITIAL_ACTIVE_PAGE, MAP_LEGEND } from '../../enums/common'
import Button from '../common/ui/Button'
import {
  ADVANCED_SEARCH_SELECT_INPUT_FIELDS,
  advancedFiltersInitialState,
} from '../../constants/filters'
import { ActiveFiltersSection } from '../pageFilters/ActiveFiltersSection'
import { fieldHasValue } from '../../utils/filters'
import { useUrlParamsHook } from '../../hooks/UseUrlParamsHook'
import { clientsFiltersActions } from '../../store/reducers/filtersReducer'
import { convertFiltersDatetime } from '../../utils/data'

export const MapSearchSection: FC<
  ISearchSection<typeof ADVANCED_SEARCH_SELECT_INPUT_FIELDS>
> = ({ setFilters, filters, changePage, fieldsData, routes, resetRoute }) => {
  const intl = useIntl()
  const searchRef = useRef<HTMLInputElement>(null)
  const [accountsPortfolioRoute, setAccountsPortfolioRoute] = useState(
    routes?.accountsPortfolio ?? ROUTES.accountsPortfolio
  )
  const dispatch = useDispatch()
  const { setUrlQueryParams, prepareUrl } = useUrlParamsHook()

  const resetAccountsPortfolioUrl = useCallback(() => {
    setAccountsPortfolioRoute(
      prepareUrl(
        {
          ...advancedFiltersInitialState,
          search: '',
          lastVisitStart: '',
          lastVisitEnd: '',
        },
        ROUTES.accountsPortfolio
      )
    )
  }, [prepareUrl])

  const handleSimpleSearch = () => {
    if (!searchRef?.current?.value) return

    if (changePage) {
      changePage(INITIAL_ACTIVE_PAGE)
    }

    dispatch(
      clientsFiltersActions.setFilters(
        convertFiltersDatetime({
          ...advancedFiltersInitialState,
          [FIELD_SEARCH]: searchRef?.current?.value ?? '',
        })
      )
    )

    dispatch(clientsFiltersActions.setSelectedPage(INITIAL_ACTIVE_PAGE))

    setFilters({
      ...advancedFiltersInitialState,
      [FIELD_SEARCH]: searchRef?.current?.value ?? '',
    })

    setUrlQueryParams(
      {
        ...advancedFiltersInitialState,
        [FIELD_SEARCH]: searchRef?.current?.value ?? '',
      },
      false
    )

    setAccountsPortfolioRoute(
      prepareUrl(
        {
          ...advancedFiltersInitialState,
          [FIELD_SEARCH]: searchRef?.current?.value ?? '',
        },
        ROUTES.accountsPortfolio
      )
    )
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSimpleSearch()
    }
  }

  // @todo move outside to a common utilities for this and PageFiltersSection
  const showFilters = useMemo(
    () =>
      !!filters &&
      !!Object.entries(filters).find(([, value]) => fieldHasValue(value)),
    [filters]
  )

  return (
    <section className="pb15 dFlex searchSection">
      <div className="relative inputFieldWrapper pr0 inputSection">
        <form>
          <input
            name={FIELD_SEARCH}
            id={FIELD_SEARCH}
            ref={searchRef}
            placeholder={intl.formatMessage({
              id: 'placeholder.searchOnMap',
            })}
            data-qa="inputSearch"
            className="inputField simpleSearchText"
            onKeyPress={handleKeyPress}
          />
        </form>
      </div>
      <div className="dFlex alignItemsCenter justifyContentBetween btnSection">
        <div className="pageFiltersAdvancedCtaWrapper dFlex">
          <DynamicFormattedMessage
            id="cta.search.button.label"
            tag={Button}
            className="btn btnPrimary"
            data-qa="searchButton"
            onClick={handleSimpleSearch}
          />
        </div>

        <div className="pl0md dFlex textRight alignItemsCenter justifyContentEnd">
          <Tooltip title="tooltip.map.title.label">
            <TooltipContent route={MAP} data={MAP_LEGEND} />
          </Tooltip>
          <Link
            to={accountsPortfolioRoute}
            className="link linkBasic dInlineFlex fontMedium"
            data-qa="ctaSeeAccountPortfolio"
          >
            <img src={iconList} alt="list icon" className="mr05" />
            <DynamicFormattedMessage id="cta.seeList.label" />
          </Link>
        </div>
      </div>
      {filters && showFilters && (
        <ActiveFiltersSection
          setFilters={setFilters}
          changePage={changePage}
          filters={filters}
          fieldsData={fieldsData}
          resetRoute={resetRoute}
          containerClassName="pl0"
          callback={resetAccountsPortfolioUrl}
        />
      )}
    </section>
  )
}
